import React from "react";
import moment from "moment";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import { ITeam } from "../../types";
import Link from "../link";
import messages from "./TeamSummaryCard.intl";
import { injectIntl } from "react-intl";
import { getNextTraining } from "../page-dashboard/nextTraining";

const CardWrapper = styled(Link)(({ theme }: IStyledArguments) => ({
  display: "block",
  textDecoration: "none",
  background: theme.palette.background.paper,
  borderRadius: 16,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  padding: theme.spacing(2.25),
  boxSizing: "border-box",
  height: "100%",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(16, 21, 26, 0.08)",
  },
}));

const HeaderRow = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 16,
});

const IdentityRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  minWidth: 0,
}));

const Crest = styled("div")(({ theme }: IStyledArguments) => ({
  width: 44,
  height: 44,
  borderRadius: 12,
  background: theme.palette.extra.accentSoft,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "0.85rem",
  color: theme.palette.primary.dark,
}));

const NameBlock = styled("div")({
  minWidth: 0,
});

const TeamName = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "1rem",
  color: theme.palette.text.primary,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const Subtitle = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.72rem",
  color: theme.palette.text.secondary,
  marginTop: 2,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StatsRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "flex-end",
  gap: theme.spacing(2),
}));

const MatchesValue = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 800,
  fontSize: "2rem",
  lineHeight: 1,
  color: theme.palette.text.primary,
  fontVariantNumeric: "tabular-nums",
  flexShrink: 0,
}));

const MatchesLabel = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.62rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: theme.palette.text.secondary,
  marginTop: 3,
}));

const RecordBar = styled("div")({
  flex: 1,
  paddingBottom: 4,
  minWidth: 0,
});

const BarTrack = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  height: 8,
  borderRadius: 4,
  overflow: "hidden",
  background: theme.palette.extra.backgroundGrey,
}));

const BarSegment = styled("div")<{ $width: number; $color: string }>(
  ({ $width, $color }: { $width: number; $color: string }) => ({
    width: `${$width}%`,
    background: $color,
  })
);

const RecordLegend = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  marginTop: 6,
  fontSize: "0.66rem",
  fontWeight: 600,
});

const FooterRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  marginTop: theme.spacing(1.75),
  paddingTop: theme.spacing(1.75),
  borderTop: `1px solid ${theme.palette.extra.borderColor}`,
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
  "& svg": {
    color: theme.palette.primary.dark,
  },
}));

const Chevron = styled("svg")(({ theme }: IStyledArguments) => ({
  color: theme.palette.primary.main,
  flexShrink: 0,
}));

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

const getNumberOfMatchesWon = (matches: any) => {
  if (!matches) return 0;
  return matches
    .map((match: any) => (match && match.isWon ? 1 : 0))
    .reduce((acc: any, current: any) => acc + current, 0);
};

const getNumberOfMatchesTied = (matches: any) => {
  if (!matches) return 0;
  return matches
    .map((match: any) => (match && match.isTied ? 1 : 0))
    .reduce((acc: any, current: any) => acc + current, 0);
};

const getNumberOfMatchesLost = (matches: any) => {
  if (!matches) return 0;
  return matches
    .map((match: any) => (match && match.isLost ? 1 : 0))
    .reduce((acc: any, current: any) => acc + current, 0);
};

interface ITeamSummaryCardProps {
  id: string;
  team: ITeam;
}

const TeamSummaryCard = injectIntl<ITeamSummaryCardProps>(({ id, team, intl }) => {
  const matchesWon = getNumberOfMatchesWon(team.matches);
  const matchesTied = getNumberOfMatchesTied(team.matches);
  const matchesLost = getNumberOfMatchesLost(team.matches);
  const totalMatches = (team.matches && team.matches.length) || 0;

  const wonPct = totalMatches ? (matchesWon / totalMatches) * 100 : 0;
  const tiedPct = totalMatches ? (matchesTied / totalMatches) * 100 : 0;
  const lostPct = totalMatches ? (matchesLost / totalMatches) * 100 : 0;

  const nextTraining = getNextTraining(team.trainingSetup);

  return (
    <CardWrapper to={`/teams/${team.id}`} data-testid={`group-summary-${id}`}>
      <HeaderRow>
        <IdentityRow>
          <Crest>{getInitials(team.name)}</Crest>
          <NameBlock>
            <TeamName>{team.name}</TeamName>
            <Subtitle>
              {team.squad ? `${team.squad.length} ${intl.formatMessage(messages.players)}` : ""}
              {" · "}
              {(team.league && team.league.name) || intl.formatMessage(messages.noLeague)}
            </Subtitle>
          </NameBlock>
        </IdentityRow>
        <Chevron
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="9 6 15 12 9 18" />
        </Chevron>
      </HeaderRow>

      <StatsRow>
        <div>
          <MatchesValue>{totalMatches}</MatchesValue>
          <MatchesLabel>{intl.formatMessage(messages.matchesLabel)}</MatchesLabel>
        </div>
        {totalMatches > 0 && (
          <RecordBar>
            <BarTrack>
              <BarSegment $width={wonPct} $color="#1B8A5A" />
              <BarSegment $width={tiedPct} $color="#9A7A2C" />
              <BarSegment $width={lostPct} $color="#B94438" />
            </BarTrack>
            <RecordLegend>
              <span style={{ color: "#1B8A5A" }}>
                {matchesWon}
                {intl.formatMessage(messages.wonAbbr)}
              </span>
              <span style={{ color: "#9A7A2C" }}>
                {matchesTied}
                {intl.formatMessage(messages.drawnAbbr)}
              </span>
              <span style={{ color: "#B94438" }}>
                {matchesLost}
                {intl.formatMessage(messages.lostAbbr)}
              </span>
            </RecordLegend>
          </RecordBar>
        )}
      </StatsRow>

      <FooterRow>
        {nextTraining ? (
          <>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3.2 2" />
            </svg>
            <span>
              {intl.formatMessage(messages.nextTraining)}{" "}
              <strong style={{ color: "#10151A", fontWeight: 600 }}>
                {moment(nextTraining.date).format("ddd")} {nextTraining.time}
              </strong>
            </span>
          </>
        ) : totalMatches === 0 ? (
          <span>{intl.formatMessage(messages.noMatchesPlayed)}</span>
        ) : (
          <span>{intl.formatMessage(messages.noTrainingSet)}</span>
        )}
      </FooterRow>
    </CardWrapper>
  );
});

export default TeamSummaryCard;
